import React from 'react';
import { ModalStatusType } from '../../../types/common';
import { ErrorMessage, Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import _ from 'lodash';
import { useWealthlaneCustomerApi } from '../../../hooks';
import { ROLE_MAPPING_VALIDATION } from '../../../utils/validations/validations';
import useGetSelfRoles from '../../../hooks/get/useGetSelfRoles';
import useGetCustomerSuccessRoleMapping from '../../../hooks/get/useGetCustomerSuccessRoleMapping';
import usePostAllowedCompanyMappingManage from '../../../hooks/post/usePostAllowedCompanyMappingManage';
import { DynamicTable, withDynamicTableContext } from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';

interface RoleMappingModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
}

const schema = {
  columns: [
    {
      Header: 'Source Company',
      id: 'sourceCompanyName',
      accessor: 'name',
      width: 300,
      sortable: false,
    },
    {
      Header: 'Published Role',
      id: 'roleName',
      accessor: 'roleName',
      width: 300,
      sortable: false,
    },
  ],
  options: {
    showToolbar: false,
    showPagination: false,
    showFilter: false,
  },
};

const RoleMappingModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
}: RoleMappingModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();

  const { data: selfRolesOption, isLoading: isSelfRolesLoading } = useGetSelfRoles(handleRequest);
  const { data: customerSuccessRoleMapping, status: customerSuccessRoleMappingStatus } =
    useGetCustomerSuccessRoleMapping(handleRequest);

  // const { mutate: addAllowedRoleMapping, isLoading: isRoleMapAdding } = usePostAllowedRoleMapping(
  //   handleRequest,
  //   closeModal
  // );

  const concatIdAndRoleId = (id: string, roleId: string) => `${id}....${roleId}`;

  const { mutate: addAllowedRoleMapping, isLoading: isRoleMapAdding } =
    usePostAllowedCompanyMappingManage(handleRequest, closeModal, modalStatus);

  const data =
    customerSuccessRoleMapping?.map((mapping: any) => ({
      id: concatIdAndRoleId(mapping.id, mapping.roleId),
      name: mapping.name,
      roleName: mapping.roleName,
    })) || [];

  const filteredData =
    customerSuccessRoleMapping?.map((mapping: any) => ({
      id: concatIdAndRoleId(mapping.id, mapping.roleId),
    })) || [];

  const isLoading = modalLoading || isSelfRolesLoading || isRoleMapAdding;

  const handleSubmit = (values: any): void => {
    const payload = {
      destinationRoleId: values.destinationRoleId,
      items: values.items || [],
    };

    addAllowedRoleMapping(payload);
  };

  const [checkedValues, setCheckedValues] = React.useState<string[]>([]);

  React.useEffect(() => {
    const initialChecked = (initialData?.data?.items || []).map((item: any) =>
      concatIdAndRoleId(item.sourceCompanyId, item.sourceRoleId)
    );
    setCheckedValues(initialChecked);
  }, [initialData?.data?.items]);

  // Create validation schema based on modal status
  const validationSchema = React.useMemo(() => {
    return ROLE_MAPPING_VALIDATION(modalStatus);
  }, [modalStatus]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialData.data,
        items: initialData.data.items || [],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        const onChecked = (keys: string[]) => {
          if (modalStatus === 'View') return;
          setCheckedValues(keys);
          const newItems = keys.map((key: string) => {
            const mapping = customerSuccessRoleMapping.find(
              (m: any) => concatIdAndRoleId(m.id, m.roleId) === key
            );
            const [companyId, roleId] = key.split('....'); // split the key by '....'
            return {
              sourceCompanyId: companyId,
              sourceRoleId: roleId,
              sourceCompanyName: mapping?.name,
              sourceRoleName: mapping?.roleName,
            };
          });
          setFieldValue('items', newItems);
        };

        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={isSelfRolesLoading}
            size='md'
            id='roleMappingModal'
            className={'role-mapping-modal'}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Role Mapping`}
            valueEdited={_.isEqual(values, initialData.data)}
            children={
              <Form>
                <ModalBodyComponent>
                  <div className=''>
                    <FormSelect
                      type='text'
                      id='destinationRoleId'
                      label={'Mapped Role'}
                      name='destinationRoleId'
                      containerClassName='formGroup'
                      onChange={(value: string) => {
                        setFieldValue('destinationRoleId', value ?? null);
                      }}
                      errors={modalStatus === 'View' ? {} : errors}
                      touched={touched}
                      options={selfRolesOption ?? []}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isClearable
                      required
                    />

                    <DynamicTable
                      schema={schema}
                      data={data}
                      totalCounts={customerSuccessRoleMapping?.length}
                      loading={customerSuccessRoleMappingStatus}
                      showCheckBox={modalStatus !== 'View'}
                      filteredData={filteredData}
                      filteredKey='id'
                      checkedValues={checkedValues}
                      onChecked={onChecked}
                      id='roleMappingTable'
                    />

                    <ErrorMessage name={'items'} component='div' className='field-error-message' />
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  modalStatus={modalStatus}
                  loading={isLoading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                  // checkError={
                  //   _.isEqual(values, initialData.data) ||
                  //   !Boolean(customerSuccessRoleMapping?.length)
                  // }
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default withDynamicTableContext(RoleMappingModal, false, axiosInstance);
